import { API, BACKEND_URL } from '@/config/api';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import AdminNavigation from '@/components/AdminNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft, Calendar, MapPin, Building2, FileText, Microscope, Edit2 as Edit, Send, CheckCircle, Download, History, X } from 'lucide-react';
import { toast } from 'sonner';
import { VersionHistoryModal } from './AdminBulkHistory/VersionHistoryModal';
import RichTextEditor from '@/components/RichTextEditor';

/** Render markdown text as JSX with proper formatting */
function renderSummaryMarkdown(text) {
  if (!text) return null;
  // Clean up HTML entities and tags from Word paste / AI generation
  const cleaned = text.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/<\/?[a-zA-Z][^>]*>/g, '');  const lines = cleaned.split('\n');
  const elements = [];
  let key = 0;

  for (const line of lines) {
    key++;
    if (line.startsWith('### ')) {
      elements.push(<h4 key={key} className="font-semibold text-slate-900 text-[15px] mt-5 mb-1.5">{renderBold(line.slice(4))}</h4>);
    } else if (line.startsWith('## ')) {
      elements.push(<h3 key={key} className="font-bold text-slate-900 text-base mt-5 mb-2">{renderBold(line.slice(3))}</h3>);
    } else if (line.startsWith('---')) {
      elements.push(<hr key={key} className="my-4 border-slate-200" />);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(
        <div key={key} className="flex gap-2.5 ml-1 my-1">
          <span className="text-teal-500 mt-1 shrink-0 text-xs">●</span>
          <span className="text-slate-700">{renderBold(line.slice(2))}</span>
        </div>
      );
    } else if (line.trim() === '') {
      elements.push(<div key={key} className="h-3" />);
    } else {
      elements.push(<p key={key} className="my-1 text-slate-700">{renderBold(line)}</p>);
    }
  }
  return elements;
}

function renderBold(text) {
  const parts = [];
  let remaining = text;
  let k = 0;
  while (remaining) {
    const m = remaining.match(/\*\*(.+?)\*\*/);
    if (m) {
      const idx = remaining.indexOf(m[0]);
      if (idx > 0) parts.push(<span key={k++}>{remaining.slice(0, idx)}</span>);
      parts.push(<strong key={k++} className="font-semibold text-slate-900">{m[1]}</strong>);
      remaining = remaining.slice(idx + m[0].length);
    } else {
      parts.push(<span key={k++}>{remaining}</span>);
      break;
    }
  }
  return parts;
}

export default function AdminBulkView() {
  const { bulkId } = useParams();
  const [bulkData, setBulkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingToCustomer, setSendingToCustomer] = useState(false);
  const [sentToCustomer, setSentToCustomer] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showSummaryEdit, setShowSummaryEdit] = useState(false);
  const [editSummaryText, setEditSummaryText] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);
  const [thresholdMap, setThresholdMap] = useState({});
  const [groupThresholdMap, setGroupThresholdMap] = useState({});
  const thresholdMapRef = useRef({});
  const groupThresholdMapRef = useRef({});

  const getLocationsFromData = () => {
    if (!bulkData) return [];
    
    // Priority 1: Use template locations (source of truth for the area)
    if (bulkData.templateLocations && bulkData.templateLocations.length > 0) {
      return bulkData.templateLocations;
    }
    
    // Priority 2: Extract from algae data
    if (bulkData.algaeData) {
      for (const orgData of Object.values(bulkData.algaeData)) {
        if (orgData && typeof orgData === 'object') {
          const locs = Object.keys(orgData);
          if (locs.length > 0) return locs;
        }
      }
    }
    
    // Priority 3: Extract from zooplankton data
    if (bulkData.zooplanktonData) {
      for (const orgData of Object.values(bulkData.zooplanktonData)) {
        if (orgData && typeof orgData === 'object') {
          const locs = Object.keys(orgData);
          if (locs.length > 0) return locs;
        }
      }
    }
    
    return [];
  };
  
  const locations = bulkData ? getLocationsFromData() : [];
  
  const getShortLocationName = (fullName) => {
    // Handle "Lokalitet A - Name" format
    let name = fullName;
    if (name.startsWith('Lokalitet ')) {
      name = name.substring('Lokalitet '.length);
    }
    // Now handle "A - Name" format
    const match = name.match(/- (.+)$/);
    return match ? match[1] : name;
  };

  const organisms = bulkData ? (() => {
    // Build organisms dynamically from saved algae data
    const dataKeys = Object.keys(bulkData.algaeData || {});
    if (dataKeys.length === 0) return [];
    
    const bTMap = bulkData.thresholdMap || {};
    const bGMap = bulkData.groupThresholdMap || {};
    
    // If template organisms are available, use them to filter
    const templateOrgKeys = bulkData.templateOrganisms 
      ? new Set(bulkData.templateOrganisms.map(o => `${o.group}_${o.species}`))
      : null;
    const templateGroups = bulkData.templateOrganisms
      ? new Set(bulkData.templateOrganisms.map(o => o.group))
      : null;
    
    // Build list - filter by template if available, otherwise show organisms with data
    const rawOrgs = [];
    dataKeys.forEach(key => {
      const parts = key.split('_');
      const group = parts[0];
      const species = parts.slice(1).join('_');
      const isSum = species.toUpperCase().includes('SUM');
      
      // Filter by template if available
      if (templateOrgKeys) {
        if (isSum) {
          if (!templateGroups.has(group)) return;
        } else {
          if (!templateOrgKeys.has(key)) return;
        }
      }
      
      const thr = isSum ? (bGMap[group] || {}) : (bTMap[key] || {});
      
      rawOrgs.push({
        group,
        species,
        depth: isSum ? '' : (thr.depth || ''),
        isSpecies: !isSum,
        isSum,
        threshold_yellow: thr.threshold_yellow,
        threshold_orange: thr.threshold_orange,
        threshold_red: thr.threshold_red
      });
    });
    
    // If template filtered everything out, fall back to showing all from data
    if (rawOrgs.length === 0 && dataKeys.length > 0) {
      dataKeys.forEach(key => {
        const parts = key.split('_');
        const group = parts[0];
        const species = parts.slice(1).join('_');
        const isSum = species.toUpperCase().includes('SUM');
        const thr = isSum ? (bGMap[group] || {}) : (bTMap[key] || {});
        rawOrgs.push({ group, species, depth: isSum ? '' : (thr.depth || ''), isSpecies: !isSum, isSum, ...thr });
      });
    }
    
    // Sort: group organisms by their group, with SUM rows at end of each group
    // Use the desired group order
    const groupOrder = ['Kiselalger', 'Svepeflagellater', 'Nåleflagellater', 'Kiselflagellater', 'Fureflagellater', 'Kalkflagellater', 'Grønnalger', 'Ciliater'];
    
    rawOrgs.sort((a, b) => {
      const aIdx = groupOrder.indexOf(a.group);
      const bIdx = groupOrder.indexOf(b.group);
      const aOrder = aIdx >= 0 ? aIdx : groupOrder.length;
      const bOrder = bIdx >= 0 ? bIdx : groupOrder.length;
      
      if (aOrder !== bOrder) return aOrder - bOrder;
      if (a.group !== b.group) return a.group.localeCompare(b.group);
      // Within same group: species first, then sum
      if (a.isSum && !b.isSum) return 1;
      if (!a.isSum && b.isSum) return -1;
      return 0;
    });
    
    return rawOrgs;
  })() : [];

  const getZooplanktonOrganismsFromData = () => {
    if (!bulkData || !bulkData.zooplanktonData) return [];
    
    // Get template zooplankton for filtering
    const templateZooKeys = bulkData.templateZooplankton
      ? new Set(bulkData.templateZooplankton.map(z => `${z.sampleType}_${z.organism}`))
      : null;
    
    const orgs = [];
    Object.keys(bulkData.zooplanktonData).forEach(key => {
      // Filter by template if available
      if (templateZooKeys && !templateZooKeys.has(key)) return;
      
      const parts = key.split('_');
      const sampleType = parts[0];
      const organism = parts.slice(1).join('_');
      if (sampleType && organism) {
        orgs.push({ sampleType, organism, key });
      }
    });
    
    // Sort by standard order, then by group, then by name
    const standardOrder = [
      'Håvtrekk_Mikromaneter', 'Håvtrekk_Kolonimaneter', 'Håvtrekk_Små maneter',
      'Håvtrekk_Radiolaria', 'Håvtrekk_Pigghudlarver', 'Håvtrekk_Foraminifera',
      'Håvtrekk_Hydroider', 'Håvtrekk_Larver skjell', 'Håvtrekk_Ribbemanet',
      'Visuelt_Manet'
    ];
    orgs.sort((a, b) => {
      const idxA = standardOrder.indexOf(a.key);
      const idxB = standardOrder.indexOf(b.key);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      if (a.sampleType !== b.sampleType) return a.sampleType.localeCompare(b.sampleType);
      return a.organism.localeCompare(b.organism);
    });
    
    return orgs;
  };

  const fetchBulkData = useCallback(async () => {
    
    try {
      const savedBulk = (await axios.get(`${API}/bulk-registrations/${bulkId}`, { withCredentials: true })).data;
      
      // Fetch thresholds separately to avoid one failure killing both
      let tMap = {};
      let gMap = {};
      try {
        const algaeStructRes = await axios.get(`${API}/organisms/algae-structure`, { withCredentials: true });
        Object.entries(algaeStructRes.data || {}).forEach(([group, groupData]) => {
          const speciesList = Array.isArray(groupData) ? groupData : (groupData?.species || []);
          speciesList.forEach(sp => {
            tMap[`${group}_${sp.species}`] = {
              threshold_yellow: sp.threshold_yellow,
              threshold_orange: sp.threshold_orange,
              threshold_red: sp.threshold_red,
              depth: sp.depth
            };
          });
          const gt = groupData?.group_thresholds;
          if (gt) {
            gMap[group] = {
              threshold_yellow: gt.threshold_yellow,
              threshold_orange: gt.threshold_orange,
              threshold_red: gt.threshold_red
            };
          }
        });
      } catch (e) {
        console.warn('Could not fetch thresholds:', e);
      }
      thresholdMapRef.current = tMap;
      groupThresholdMapRef.current = gMap;
      setThresholdMap(tMap);
      setGroupThresholdMap(gMap);
      
      // Find matching template to get correct locations for this area
      let templateLocations = null;
      try {
        const templatesRes = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
        const areaName = (savedBulk.area_name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const match = (templatesRes.data || []).find(t => {
          const tName = (t.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          return areaName && tName && (tName.includes(areaName) || areaName.includes(tName));
        });
        if (match?.config?.locations?.length > 0) {
          templateLocations = match.config.locations.map(l => l.full_name);
        }
        var templateOrganisms = match?.config?.organisms || null;
        var templateZooplankton = match?.config?.zooplankton || null;
      } catch {}
      
      setBulkData({
        id: bulkId,
        date: savedBulk.date || savedBulk.sample_date,
        area: savedBulk.area_name || savedBulk.area,
        areaId: savedBulk.area_id,
        biologist: savedBulk.biologist || savedBulk.created_by,
        status: savedBulk.status,
        title: `Planktonanalyse - ${savedBulk.area_name || savedBulk.area}`,
        algaeData: savedBulk.algae_data || {},
        zooplanktonData: savedBulk.zooplankton_data || {},
        colorOverrides: savedBulk.color_overrides || {},
        temperatureData: savedBulk.temperature_data || {},
        aiSummary: savedBulk.ai_summary,
        sentToCustomer: savedBulk.sent_to_customer || false,
        thresholdMap: tMap,
        groupThresholdMap: gMap,
        templateLocations: templateLocations,
        templateOrganisms: typeof templateOrganisms !== 'undefined' ? templateOrganisms : null,
        templateZooplankton: typeof templateZooplankton !== 'undefined' ? templateZooplankton : null
      });
      setSentToCustomer(savedBulk.sent_to_customer || false);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bulk data from API:', error);
      
      setBulkData({
        id: bulkId,
        date: new Date().toISOString().split('T')[0],
        area: 'Ukjent område',
        biologist: 'Ukjent',
        status: 'normal',
        title: 'Registrering ikke funnet',
        algaeData: {},
        zooplanktonData: {},
        colorOverrides: {}
      });
      setLoading(false);
    }
  }, [bulkId]);

  useEffect(() => {
    fetchBulkData();
  }, [fetchBulkData]);

  const getColorForValue = (value, overrides, group, species, location, organism = null) => {
    const overrideKey = `${group}_${species}_${location}`;
    const strValue = String(value).trim();
    
    const isBelow = strValue.startsWith('<');
    if (isBelow || strValue === '0' || strValue === '+') return 'bg-emerald-100 text-emerald-900';
    
    const cleanValue = strValue.replace(/[<>\s\u00A0]/g, '').replace(/,/g, '.');
    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) return value ? 'bg-emerald-100 text-emerald-900' : 'bg-white';
    if (numValue === 0) return 'bg-emerald-100 text-emerald-900';
    
    // Use thresholds from organism object, or fallback to well-known defaults for sum rows
    const isSum = species.toUpperCase().includes('SUM');
    
    let tY = 0, tO = 0, tR = 0;
    let hasThresholds = false;
    
    if (organism && organism.threshold_red != null) {
      tY = organism.threshold_yellow || 0;
      tO = organism.threshold_orange || 0;
      tR = organism.threshold_red || 0;
      hasThresholds = true;
    } else if (isSum && group === 'Kiselalger') {
      // Well-known defaults for Kiselalger sum
      tY = 1000000; tO = 5000000; tR = 20000000;
      hasThresholds = true;
    }
    
    let thresholdColor = null;
    if (hasThresholds) {
      if (numValue >= tR) thresholdColor = 'red';
      else if (numValue >= tO) thresholdColor = 'orange';
      else if (numValue >= tY) thresholdColor = 'yellow';
      else thresholdColor = 'green';
    }
    
    const override = overrides?.[overrideKey];
    const effectiveColor = (override && thresholdColor && override !== thresholdColor) ? override : (thresholdColor || override || 'green');
    
    const colorMap = {
      'green': 'bg-emerald-100 text-emerald-900',
      'yellow': 'bg-amber-100 text-amber-900',
      'orange': 'bg-orange-200 text-orange-900',
      'red': 'bg-red-200 text-red-900'
    };
    return colorMap[effectiveColor] || 'bg-emerald-100 text-emerald-900';
  };

  const handleSendToCustomer = async () => {
    if (sentToCustomer) return;
    
    setSendingToCustomer(true);
    try {
      const response = await axios.post(
        `${API}/bulk-registrations/${bulkId}/send-to-customer`,
        {},
        { withCredentials: true }
      );
      
      setSentToCustomer(true);
      
      const notificationCount = response.data.notifications?.notifications_sent || 0;
      
      if (notificationCount > 0) {
        toast.success('Prøve sendt til kunde!', {
          description: `${notificationCount} bruker(e) har blitt varslet via SMS`
        });
      } else {
        toast.success('Prøve sendt til kunde!', {
          description: 'Ingen brukere hadde aktivert SMS-varsling'
        });
      }
    } catch (error) {
      console.error('Error sending to customer:', error);
      toast.error('Kunne ikke sende til kunde', {
        description: error.response?.data?.detail || 'Prøv igjen senere'
      });
    } finally {
      setSendingToCustomer(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/reports/bulk/${bulkId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Microsafe_${bulkData.area}_${bulkData.date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF-rapport lastet ned');
    } catch (error) {
      let errorMsg = 'Kunne ikkje laste ned PDF';
      try {
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.detail || errorMsg;
        }
      } catch {}
      console.error('PDF error:', errorMsg, error.response?.status);
      toast.error(errorMsg);
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <>
        <AdminNavigation />
        <div className="flex items-center justify-center min-h-screen bg-white">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Laster bulk data...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavigation />
      <div className="min-h-screen bg-white lg:pl-[240px]">
        <div className="max-w-full mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <Link to="/admin/bulk-history">
                <Button variant="ghost">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Tilbake til bulk historikk
                </Button>
              </Link>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 mb-2">{bulkData.title}</h1>
                <div className="flex items-center space-x-4 text-slate-600">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(bulkData.date).toLocaleDateString('nb-NO')}</span>
                  </div>
                  <div>{bulkData.area}</div>
                  <div>Biolog: {bulkData.biologist}</div>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  data-testid="download-pdf-button"
                >
                  {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Last ned PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowVersionHistory(true)}
                  data-testid="view-version-history-btn"
                  className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                >
                  <History className="w-4 h-4 mr-2" />
                  Historikk
                </Button>
                <Link to={`/admin/bulk-edit/${bulkData.id}`}>
                  <Button variant="outline">
                    <Edit className="w-4 h-4 mr-2" />
                    Rediger registrering
                  </Button>
                </Link>
                <Button 
                  onClick={handleSendToCustomer}
                  disabled={sendingToCustomer || sentToCustomer}
                  className={sentToCustomer 
                    ? "bg-emerald-600 hover:bg-emerald-600 cursor-default" 
                    : "bg-teal-600 hover:bg-teal-700"
                  }
                >
                  {sendingToCustomer ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sender...
                    </>
                  ) : sentToCustomer ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Sendt til kunde
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send til kunde
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
          
          {/* Lab Report — inline view with edit toggle */}
          <Card className="my-6 border-slate-200 overflow-hidden" data-testid="lab-report-section">
            <CardContent className="p-0">
              {/* Header bar */}
              <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Laboratorierapport</h3>
                    <p className="text-xs text-slate-400 mt-0.5">{showSummaryEdit ? 'Redigeringsmodus' : 'AI-generert rapport'}</p>
                  </div>
                </div>
                {!showSummaryEdit ? (
                  <button
                    onClick={() => { setEditSummaryText(bulkData?.aiSummary || ''); setShowSummaryEdit(true); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition-all"
                    data-testid="report-edit-toggle"
                  >
                    <Edit className="w-3.5 h-3.5" /> Rediger
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowSummaryEdit(false)}>
                      Avbryt
                    </Button>
                    <Button
                      size="sm"
                      disabled={savingSummary}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="report-save-inline-btn"
                      onClick={async () => {
                        setSavingSummary(true);
                        try {
                          const fullBulk = await axios.get(`${API}/bulk-registrations/${bulkId}`, { withCredentials: true });
                          await axios.put(`${API}/bulk-registrations/${bulkId}`, {
                            ...fullBulk.data,
                            ai_summary: editSummaryText.replace(/&nbsp;/g, ' ')
                          }, { withCredentials: true });
                          toast.success('Rapport lagra');
                          setShowSummaryEdit(false);
                          fetchBulkData();
                        } catch {
                          toast.error('Kunne ikkje lagre');
                        } finally { setSavingSummary(false); }
                      }}
                    >
                      {savingSummary ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
                      Lagre rapport
                    </Button>
                  </div>
                )}
              </div>

              {/* Content — view or edit inline */}
              {showSummaryEdit ? (
                <div className="p-6">
                  <RichTextEditor
                    content={editSummaryText}
                    onChange={setEditSummaryText}
                    placeholder="Skriv laboratorierapport her..."
                  />
                </div>
              ) : (
                <div className="px-6 py-5">
                  {bulkData?.aiSummary ? (
                    <div className="text-[14px] leading-[1.8] text-slate-700 max-w-none
                      [&_h3]:font-bold [&_h3]:text-slate-900 [&_h3]:text-base [&_h3]:mt-5 [&_h3]:mb-2
                      [&_h4]:font-semibold [&_h4]:text-teal-800 [&_h4]:text-[15px] [&_h4]:mt-5 [&_h4]:mb-1.5
                      [&_hr]:my-4 [&_hr]:border-slate-200">
                      {renderSummaryMarkdown(bulkData.aiSummary)}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <FileText className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                      <p className="text-sm text-slate-400 mb-3">Ingen rapport enno</p>
                      <Button size="sm" variant="outline" onClick={() => { setEditSummaryText(''); setShowSummaryEdit(true); }}>
                        <Edit className="w-3.5 h-3.5 mr-1.5" /> Skriv rapport
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Algae Results Table */}
          <Card className="shadow-xl border-0 mb-8">
            <CardHeader>
              <CardTitle className="text-2xl">Algeresultater</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="border border-slate-300 px-4 py-3 text-left font-bold">Orden/gruppe</th>
                      <th className="border border-slate-300 px-4 py-3 text-left font-bold">Evt. art</th>
                      <th className="border border-slate-300 px-3 py-3 text-center font-bold">Djup (m)</th>
                      {locations.map(loc => {
                        const temp = bulkData?.temperatureData?.[loc] || bulkData?.temperature_data?.[loc];
                        return (
                          <th key={loc} className="border border-slate-300 px-3 py-2 text-center font-bold min-w-24">
                            <div className="text-sm">{getShortLocationName(loc)}</div>
                            <div className="text-xs text-slate-500">(ant/l)</div>
                            {temp && (
                              <div className="mt-0.5 text-[10px] font-semibold text-sky-600 bg-sky-50 rounded-full px-2 py-0.5 inline-block">
                                {temp}°C
                              </div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {organisms.map(organism => {
                      const isSum = organism.isSum;
                      const key = organism.group + (organism.species ? '_' + organism.species : '');
                      
                      return (
                        <tr key={key} className={isSum ? 'bg-slate-100 font-semibold' : ''}>
                          <td className="border border-slate-300 px-4 py-3">{organism.group}</td>
                          <td className="border border-slate-300 px-4 py-3 italic">{organism.species}</td>
                          <td className="border border-slate-300 px-3 py-3 text-center">{organism.depth}</td>
                          {locations.map(location => {
                            const value = bulkData.algaeData[key]?.[location] || '';
                            const hasValue = value && value.trim() !== '';
                            
                            // Inline color computation - more reliable than function call during re-renders
                            let cellColor = 'bg-slate-50 text-slate-400';
                            if (hasValue) {
                              const sv = String(value).trim();
                              if (sv.startsWith('<') || sv === '0' || sv === '+') {
                                cellColor = 'bg-emerald-100 text-emerald-900';
                              } else {
                                const nv = parseFloat(sv.replace(/[\s\u00A0]/g, '').replace(/,/g, '.'));
                                if (!isNaN(nv) && nv > 0) {
                                  // Get thresholds
                                  const isSumRow = organism.species?.toUpperCase().includes('SUM');
                                  const thr = isSumRow ? (bulkData.groupThresholdMap?.[organism.group]) : (bulkData.thresholdMap?.[key]);
                                  const tY = thr?.threshold_yellow || (isSumRow && organism.group === 'Kiselalger' ? 1000000 : 0);
                                  const tO = thr?.threshold_orange || (isSumRow && organism.group === 'Kiselalger' ? 5000000 : 0);
                                  const tR = thr?.threshold_red || (isSumRow && organism.group === 'Kiselalger' ? 20000000 : 0);
                                  
                                  if (tR > 0) {
                                    if (nv >= tR) cellColor = 'bg-red-200 text-red-900';
                                    else if (nv >= tO) cellColor = 'bg-orange-200 text-orange-900';
                                    else if (nv >= tY) cellColor = 'bg-amber-100 text-amber-900';
                                    else cellColor = 'bg-emerald-100 text-emerald-900';
                                  } else {
                                    cellColor = 'bg-emerald-100 text-emerald-900';
                                  }
                                  
                                  // Check for manual override (skip for sum rows - use threshold only)
                                  if (!isSumRow) {
                                    const ov = bulkData.colorOverrides?.[`${key}_${location}`];
                                    if (ov) {
                                      const ovMap = { 'green': 'bg-emerald-100 text-emerald-900', 'yellow': 'bg-amber-100 text-amber-900', 'orange': 'bg-orange-200 text-orange-900', 'red': 'bg-red-200 text-red-900' };
                                      cellColor = ovMap[ov] || cellColor;
                                    }
                                  }
                                } else {
                                  cellColor = 'bg-emerald-100 text-emerald-900';
                                }
                              }
                            } else if (isSum) {
                              cellColor = 'bg-slate-200 text-slate-500';
                            }
                            
                            return (
                              <td key={location} className="border border-slate-300 p-2">
                                <div className={`h-10 flex items-center justify-center text-sm font-semibold rounded-md ${cellColor}`}>
                                  {hasValue ? value : '—'}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Zooplankton Results Table */}
          <Card className="shadow-xl border-0">
            <CardHeader>
              <CardTitle className="text-2xl">Zooplankton resultater</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="border border-slate-300 px-4 py-3 text-left font-bold">Prøvetype</th>
                      <th className="border border-slate-300 px-4 py-3 text-left font-bold">Organisme</th>
                      {locations.map(loc => {
                        const temp = bulkData?.temperatureData?.[loc];
                        return (
                          <th key={loc} className="border border-slate-300 px-3 py-2 text-center font-bold min-w-24">
                            <div className="text-sm">{getShortLocationName(loc)}</div>
                            <div className="text-xs text-slate-500">(ant/m³)</div>
                            {temp && (
                              <div className="mt-0.5 text-[10px] font-semibold text-sky-600 bg-sky-50 rounded-full px-2 py-0.5 inline-block">
                                {temp}°C
                              </div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {getZooplanktonOrganismsFromData().map(organism => {
                      const key = `${organism.sampleType}_${organism.organism}`;
                      
                      return (
                        <tr key={key} className="hover:bg-slate-50">
                          <td className="border border-slate-300 px-4 py-3">{organism.sampleType}</td>
                          <td className="border border-slate-300 px-4 py-3 italic">{organism.organism}</td>
                          {locations.map(location => {
                            const value = bulkData.zooplanktonData[key]?.[location] || '';
                            const hasValue = value && value.trim() !== '';
                            
                            // Check manual color override for this zoo cell
                            let cellColor = hasValue ? 'bg-blue-50 text-blue-900' : 'bg-slate-50 text-slate-400';
                            const ov = bulkData.colorOverrides?.[`${key}_${location}`];
                            if (ov && hasValue) {
                              const ovMap = { 'green': 'bg-emerald-100 text-emerald-900', 'yellow': 'bg-amber-100 text-amber-900', 'orange': 'bg-orange-200 text-orange-900', 'red': 'bg-red-200 text-red-900' };
                              cellColor = ovMap[ov] || cellColor;
                            }
                            
                            return (
                              <td key={location} className="border border-slate-300 p-2">
                                <div className={`h-10 flex items-center justify-center text-sm font-semibold rounded-md ${cellColor}`}>
                                  {hasValue ? value : '—'}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Version history modal */}
      {showVersionHistory && (
        <VersionHistoryModal
          bulkId={bulkId}
          bulkLabel={`${bulkData?.area} — ${bulkData?.date}`}
          onClose={() => setShowVersionHistory(false)}
          onRestore={fetchBulkData}
        />
      )}

    </>
  );
}